import React from 'react';
import Select from 'react-select';
import findIndex from 'lodash/findIndex';
import { QUESTIONS } from '../../constants/config';
import { TargetNode } from '../../models/TargetNode';

// A question is a per-language array of strings; index 0 is the question text.
type Question = Record<string, string[]>;

interface QuestionOption {
  value: number;
  label: string;
}

interface QuestionSelectorProps {
  // Plain target node (the `TargetNode` model lands with the container in
  // Phase 7). Only `question` is read.
  node: TargetNode;
  className?: string;
  onChangeQuestion: (option: QuestionOption | null) => void;
}

const QuestionSelector = ({ node, className, onChangeQuestion }: QuestionSelectorProps) => {
  // Every target has a question; the model marks it optional because it models
  // server data. Nothing to pick from without one.
  const nodeQuestion = node.question;
  if (!nodeQuestion) {
    return null;
  }

  // A target may carry a question that is not one of the built-in defaults; if
  // so it is appended to the list so it can be displayed as the current value.
  const isInDefaultQuestions =
    -1 < findIndex(QUESTIONS, (question: Question) => question.en[0] === nodeQuestion.en[0]);

  const combineQuestions = (): Question[] => {
    const questionList: Question[] = ([] as Question[]).concat(QUESTIONS);
    if (!isInDefaultQuestions) {
      questionList.push(nodeQuestion);
    }
    return questionList;
  };

  const questionList: QuestionOption[] = combineQuestions()
    .map((question, i) => ({ value: i, label: question.en[0] }));

  const currentQuestion =
    findIndex(combineQuestions(), (question: Question) => question.en[0] === nodeQuestion.en[0]);

  // NOTE: this guard used to read `if (!parent)` with no such variable in scope —
  // it resolved to `window.parent`, which is always truthy, so it never fired.
  // The sibling selectors (ParentSelector, QRcodeSelector) guard on
  // `node.parent` to hide themselves on a root target, which is evidently what
  // was meant here too. Left as a no-op rather than "fixed", because enabling it
  // would make the question dropdown disappear on root targets — a visible
  // change that should be a deliberate decision, not a side effect of typing.

  return (
    <div className={className}>
      <Select
        className={currentQuestion === 0 ? 'has-value' : ''}
        value={questionList[currentQuestion]}
        options={questionList}
        onChange={onChangeQuestion} />
    </div>
  );
};

export default React.memo(QuestionSelector);
